import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import BusinessCenterRoundedIcon from "@mui/icons-material/BusinessCenterRounded";
import ChatBubbleRoundedIcon from "@mui/icons-material/ChatBubbleRounded";
import MoreTimeRoundedIcon from "@mui/icons-material/MoreTimeRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import { Box, Typography } from "@mui/material";
import { type FC, type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK, SURFACE } from "../../../theme/tokens";
import MetaChip from "../../UIToolkit/Badges/MetaChip";
import { SystemTabs } from "../../UIToolkit/Clickable/TabsAndChips/SystemTabs/SystemTabs";
import { CollapsibleSection, CollapsibleSectionVariant } from "../../UIToolkit/Panels/CollapsibleSection";
import { OutreachPersonRow } from "./OutreachPersonRow";
import { CALLS_GROUP_ID, OutreachGroupIcon, type OutreachGroupView, type OutreachRowAction, OutreachTab } from "./outreachGroupTypes";
import { TEXT_INSET } from "./outreachRowLayout";
import type { OutreachCopy } from "./useOutreachCopy";

const GROUP_ICON: Record<OutreachGroupIcon, ReactNode> = {
	[OutreachGroupIcon.Message]: <ChatBubbleRoundedIcon />,
	[OutreachGroupIcon.AppAlert]: <NotificationsActiveRoundedIcon />,
	[OutreachGroupIcon.Call]: <PhoneRoundedIcon />,
	[OutreachGroupIcon.Overtime]: <MoreTimeRoundedIcon />,
	[OutreachGroupIcon.Agency]: <BusinessCenterRoundedIcon />,
	[OutreachGroupIcon.Skipped]: <BlockOutlinedIcon />,
};

type Props = {
	groups: Record<OutreachTab, OutreachGroupView[]>;
	tab: OutreachTab;
	onTabChange: (tab: OutreachTab) => void;
	copy: OutreachCopy;
	calling: boolean;
	sendsDisabled: boolean;
	onAction: (action: OutreachRowAction, nurseId: number) => void;
};

/** People, not rows: someone can be in two groups (texted, then called). Groups that don't count (an app alert, a call preview) are left out. */
const countOf = (groups: readonly OutreachGroupView[]) =>
	new Set(groups.filter((group) => group.countsInTab).flatMap((group) => group.rows.map((row) => row.nurseId))).size;

/** Reached · Up next · Not texting, each a stack of collapsible groups; the legend shows once someone's Why is open. */
export const OutreachPeopleList: FC<Props> = ({ groups, tab, onTabChange, copy, calling, sendsDisabled, onAction }) => {
	const { t } = useTranslation();
	// DEFAULT: only groups the user toggled are stored; the rest follow their own `defaultOpen`, so a group that
	// appears later (Sent by you) opens as designed.
	const [toggled, setToggled] = useState<Record<string, boolean>>({});
	const [whyOpen, setWhyOpen] = useState<ReadonlySet<string>>(new Set());
	const isOpen = (group: OutreachGroupView) => toggled[`${tab}:${group.id}`] ?? group.defaultOpen;
	const toggleWhy = (key: string) =>
		setWhyOpen((current) => {
			const next = new Set(current);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});

	const tabs = [
		{ value: OutreachTab.Reached, label: t("request.outreach.tab.reached") },
		{ value: OutreachTab.UpNext, label: t("request.outreach.tab.up_next") },
		{ value: OutreachTab.NotTexting, label: t("request.outreach.tab.not_texting") },
	].map((entry) => ({ ...entry, badge: { content: countOf(groups[entry.value]), color: "primary" as const } }));

	return (
		<Box sx={{ display: "flex", flexDirection: "column", flex: "1 0 auto" }}>
			{/* The popup body scrolls as one; the tabs pin to its top so they stay reachable over a long list. */}
			<Box
				sx={{
					position: "sticky",
					top: 0,
					zIndex: 1,
					backgroundColor: SURFACE,
					display: "flex",
					alignItems: "center",
					gap: 2,
					borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
				}}
			>
				<SystemTabs tabs={tabs} currentTab={tab} onTabChange={onTabChange} dataTestId="outreach-tabs" />
				<Box sx={{ flex: 1 }} />
				{tab !== OutreachTab.NotTexting && [...whyOpen].some((key) => key.startsWith(`${tab}:`)) && (
					<Box data-testid="outreach-legend" sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 0.75 }}>
						<MetaChip size="small" tone="success" label={t("request.outreach.legend.up")} />
						<MetaChip size="small" tone="warning" label={t("request.outreach.legend.down")} />
					</Box>
				)}
			</Box>
			{/* No tabpanel role: SystemTabs doesn't expose tab ids to label it by. */}
			<Box sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 1.5, pb: 2, px: 0.25 }}>
				{groups[tab].map((group) => (
					<CollapsibleSection
						key={group.id}
						variant={CollapsibleSectionVariant.Card}
						title={group.title}
						meta={group.meta}
						icon={GROUP_ICON[group.icon]}
						open={isOpen(group)}
						onToggle={() => setToggled((current) => ({ ...current, [`${tab}:${group.id}`]: !isOpen(group) }))}
						dataTestId={`outreach-group-${group.id}`}
					>
						{group.note !== undefined && (
							<Typography sx={{ mx: 1.75, pb: 1, ps: TEXT_INSET, pe: 0.5, fontSize: FONT_SIZE.SECONDARY, lineHeight: 1.5, color: INK.SECONDARY }}>
								{group.note}
							</Typography>
						)}
						{group.rows.map((row) => {
							const key = `${tab}:${group.id}:${row.nurseId}`;
							return (
								<OutreachPersonRow
									key={row.nurseId}
									row={row}
									copy={copy}
									calling={calling}
									sendsDisabled={sendsDisabled}
									whyOpen={whyOpen.has(key)}
									onToggleWhy={() => toggleWhy(key)}
									onAction={onAction}
									dataTestId={group.id.startsWith(CALLS_GROUP_ID) ? `outreach-call-${row.nurseId}` : `outreach-person-${row.nurseId}`}
								/>
							);
						})}
					</CollapsibleSection>
				))}
			</Box>
		</Box>
	);
};
